<script setup>
// Barra "Passo N de 6" do setup do Polaris AI (spec §4.2): renderizada pelo PageLayout das páginas do assistente
// enquanto o setup não está completo. Voltar/Avançar andam na ordem Roteiro → Conhecimento → Orientações →
// Integrações → Testar → Publicar. O passo mostrado é o da página atual; fora de uma página de passo, o primeiro
// passo não feito.
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ChevronLeft16, ChevronRight16 } from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { SETUP_STEP_ROUTES } from 'dashboard/routes/dashboard/polaris/setupSteps';

const props = defineProps({
  steps: { type: Array, required: true },
  assistantName: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const routeStepIndex = computed(() =>
  props.steps.findIndex(step => SETUP_STEP_ROUTES[step.key] === route.name)
);

const currentIndex = computed(() => {
  if (routeStepIndex.value >= 0) return routeStepIndex.value;
  const firstTodo = props.steps.findIndex(step => step.state !== 'done');
  return firstTodo >= 0 ? firstTodo : props.steps.length - 1;
});

const currentStep = computed(() => props.steps[currentIndex.value]);

const stepTitle = key =>
  t(`POLARIS.GET_STARTED.STEPS.${key.toUpperCase()}.TITLE`);

const label = computed(() =>
  t('POLARIS.SETUP_PROGRESS.LABEL', {
    name: props.assistantName,
    current: currentIndex.value + 1,
    total: props.steps.length,
    step: currentStep.value ? stepTitle(currentStep.value.key) : '',
  })
);

const goToStep = index => {
  const step = props.steps[index];
  if (!step) return;
  router.push({
    name: SETUP_STEP_ROUTES[step.key],
    params: {
      accountId: route.params.accountId,
      assistantId: route.params.assistantId,
    },
  });
};
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-3 border-0 border-b border-solid border-polaris-border bg-polaris-card px-6 py-2.5"
  >
    <span class="min-w-0 flex-1 truncate text-sm text-polaris-foreground">
      {{ label }}
    </span>
    <div class="flex flex-1 basis-40 gap-1" role="img" :aria-label="label">
      <span
        v-for="(step, index) in steps"
        :key="step.key"
        class="h-1.5 flex-1 rounded-full"
        :class="
          step.state === 'done'
            ? 'bg-polaris-primary'
            : index === currentIndex
              ? 'bg-polaris-primary/50'
              : 'bg-polaris-muted'
        "
      />
    </div>
    <div class="flex gap-1">
      <Button
        variant="ghost"
        size="sm"
        :disabled="currentIndex === 0"
        @click="goToStep(currentIndex - 1)"
      >
        <ChevronLeft16 aria-hidden="true" />
        {{ t('POLARIS.SETUP_PROGRESS.BACK') }}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        :disabled="currentIndex >= steps.length - 1"
        @click="goToStep(currentIndex + 1)"
      >
        {{ t('POLARIS.SETUP_PROGRESS.NEXT') }}
        <ChevronRight16 aria-hidden="true" />
      </Button>
    </div>
  </div>
</template>
